import api from './api.ts';

export interface InstagramAccount {
  id: string;
  username?: string | null;
  name?: string | null;
}

export interface InstagramConnection extends Omit<InstagramAccount, 'id'> {
  id: number | string;
  status: string;
  profile_picture_url?: string | null;
  webhook_subscribed_at?: string | null;
  last_checked_at?: string | null;
  last_error_code?: string | null;
  comments?: { enabled: boolean; permission_granted: boolean; reply_comment: boolean };
}

export interface InstagramSnapshot {
  available: boolean;
  connection: InstagramConnection | null;
  pendingAccounts?: InstagramAccount[];
}

const endpoint = '/api/integrations/instagram';

export const getInstagramConnection = async (): Promise<InstagramSnapshot> => (
  await api.get(`${endpoint}/connection`)
).data;

export const refreshInstagramConnection = async (): Promise<InstagramSnapshot> => (
  await api.post(`${endpoint}/refresh`)
).data;

export const selectInstagramAccount = async (accountId: string): Promise<InstagramSnapshot> => (
  await api.post(`${endpoint}/select-account`, { accountId })
).data;

export const disconnectInstagram = async (): Promise<InstagramSnapshot> => (
  await api.delete(`${endpoint}/connection`)
).data;

export async function getInstagramAuthorizationUrl(): Promise<string> {
  const { data } = await api.get(`${endpoint}/oauth/url`);
  const url = new URL(data.authorizationUrl);
  if (url.protocol !== 'https:' || url.username || url.password || url.port
    || !['www.facebook.com', 'www.instagram.com'].includes(url.hostname)) {
    throw new Error('Destino de autorização inválido.');
  }
  return url.href;
}

export interface InstagramContactIdentity {
  identity_id: number;
  contact_id: number;
  channel: 'instagram';
  username?: string | null;
  name?: string | null;
  photo?: string | null;
  clinic_username?: string | null;
  last_inbound_at?: string | null;
  window_expires_at?: string | null;
  window_open?: boolean;
  capabilities: { send_text: boolean; send_media_url: boolean; upload_private_media: boolean; mark_read: boolean; reply_comment?: boolean };
  send_block_reason?: 'window_closed' | 'connection_unavailable' | null;
}

export const getInstagramContactIdentity = async (contactId: number): Promise<InstagramContactIdentity> => (
  await api.get(`/api/instagram/contacts/${contactId}/identity`)
).data;

export interface InstagramSendResult {
  status: 'sent' | 'unknown' | 'failed' | 'sending';
  message_id?: number | null;
  provider_message_id?: string | null;
  error_code?: string | null;
  content?: string | null;
}

export interface InstagramCommentData {
  id: number;
  text: string;
  username?: string | null;
  media_product_type?: string;
  status: string;
  is_public_reply?: boolean;
  reply_to_comment_id?: number | null;
}

export interface InstagramCommentScope { contactId: number; identityId: number; }
export interface InstagramCommentContext {
  comment_id: number;
  status: 'available' | 'unavailable';
  permalink?: string | null;
  caption?: string | null;
  media_product_type?: string;
}

export const getInstagramCommentContext = async (id: number, scope: InstagramCommentScope): Promise<InstagramCommentContext> => (
  await api.get(`/api/instagram/comments/${id}/context`, { params: { contact_id: scope.contactId, identity_id: scope.identityId } })
).data;

export const replyInstagramComment = async (id: number, input: {
  contact_id: number; identity_id: number; idempotency_key: string; content: string;
}): Promise<InstagramSendResult> => (await api.post(`/api/instagram/comments/${id}/replies`, input)).data;

export const getInstagramCommentReplyOperation = async (scope: InstagramCommentScope, key: string): Promise<InstagramSendResult> => (
  await api.get('/api/instagram/comment-reply-operations', { params: {
    contact_id: scope.contactId, identity_id: scope.identityId, idempotency_key: key,
  } })
).data;

/** O chamador preserva a chave enquanto reconcilia uma tentativa incerta. */
export const sendInstagramMessage = async (input: {
  contact_id: number; identity_id: number; idempotency_key: string;
  content: string; message_type: 'text' | 'image' | 'video' | 'audio';
  attachment_id?: number;
}): Promise<InstagramSendResult> => (await api.post('/api/instagram/messages', input)).data;

export interface InstagramAttachmentResult {
  attachment_id: number;
  status: 'ready' | 'uploading' | 'unknown' | 'failed';
  message_type: 'image' | 'video' | 'audio';
  content_url: string;
  error_code?: string | null;
}

export async function uploadInstagramAttachment(input: { contact_id: number; identity_id: number; idempotency_key: string; file: File }): Promise<InstagramAttachmentResult> {
  const data = new FormData();
  data.append('contact_id', String(input.contact_id));
  data.append('identity_id', String(input.identity_id));
  data.append('idempotency_key', input.idempotency_key);
  data.append('file', input.file);
  return (await api.post('/api/instagram/attachments', data, { headers: { 'Content-Type': 'multipart/form-data' } })).data;
}

export const markInstagramRead = async (contactId: number): Promise<void> => {
  await api.put(`/api/instagram/contacts/${contactId}/read`);
};
